import { type Control, useController } from 'react-hook-form';
import ReactSwitch from 'react-switch';
import { Column, Row, SmartTooltip, Text } from '~/commonComponents';
import InfoIcon from '~/images/icons/Info';
import {
  fireShiftRequestAutoconfirmFavoriteSelected,
  fireShiftRequestAutoconfirmEligibleSelected,
  fireEditShiftAutoconfirmFavoriteSelected,
  fireEditShiftAutoconfirmEligibleSelected,
  fireOpenShiftAutoconfirmFavoriteSelected,
  fireOpenShiftAutoconfirmEligibleSelected,
} from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import { type IShiftDetailsFormValues } from '../ShiftDetailsForm/useShiftDetailsForm';

export type AutoConfirmEventContext = 'shift_request' | 'edit_shift' | 'open_shift';

interface IAutoConfirmFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  preferredSpecialty: string;
  eventContext?: AutoConfirmEventContext;
}

const TOOLTIP_CONTENT = (
  <Text color="white" bold>
    Professional&apos;s eligibility criteria:
    <br />
    <br />
    {'\u2022'} Has at least a 95% attendance score for previous shifts worked.
    <br />
    {'\u2022'} Has a minimum of 2 years working experience.
    <br />
    {'\u2022'} Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'\u2022'} Has completed at least 3 shifts.
  </Text>
);

export const AutoConfirmField = ({
  control,
  professionType,
  preferredSpecialty,
  eventContext,
}: IAutoConfirmFieldProps) => {
  const { field } = useController({ name: 'autoConfirm', control });

  const getAutoFillInfo = (professionType: string, specialty: string) => {
    if (['Associate Dentist', 'Front Desk'].includes(professionType)) {
      return {
        show: false,
        message: `The Auto-Confirm feature is not available for ${professionType} at this time.`,
      };
    }

    if (!specialty?.toLowerCase()?.includes('general')) {
      return {
        show: false,
        message:
          'The Auto-Confirm feature is only available for shifts without a specialty preference (General).',
      };
    }

    return {
      show: true,
      message: (
        <div>
          - Auto-Confirm does not apply to counter offers.
          <br />- Selecting “Favorites” will send them invites.
        </div>
      ),
    };
  };

  const autoFillInfo = getAutoFillInfo(professionType, preferredSpecialty);

  if (!autoFillInfo?.show) {
    return (
      <Column data-testid="auto-confirm-field" data-cy="auto-confirm-field" gap={12}>
        <Row alignItems="center" gap={6}>
          <Text bold fontSize={18} color="neutral_600">
            Auto-Confirm:
          </Text>
        </Row>
        <Text
          fontSize={16}
          color="neutral_500"
          data-testid="auto-confirm-unavailable-message"
          data-cy="auto-confirm-unavailable-message"
          italic
        >
          The Auto-Confirm feature is only available for shifts without a specialty preference
          (General).
        </Text>
      </Column>
    );
  }

  const fireToggleEvent = (key: 'favorites' | 'eligibleProfessionals', newValue: boolean) => {
    if (!eventContext) return;

    const payload = {
      autoConfirm: (newValue ? 'on' : 'off') as 'on' | 'off',
      designDate: 202603,
    };

    const eventMap = {
      shift_request: {
        favorites: fireShiftRequestAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireShiftRequestAutoconfirmEligibleSelected,
      },
      edit_shift: {
        favorites: fireEditShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireEditShiftAutoconfirmEligibleSelected,
      },
      open_shift: {
        favorites: fireOpenShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireOpenShiftAutoconfirmEligibleSelected,
      },
    };

    eventMap[eventContext][key](payload);
  };

  const handleToggle = (key: 'favorites' | 'eligibleProfessionals') => {
    const current = field.value ?? { favorites: false, eligibleProfessionals: false };
    const newValue = !current[key];
    field.onChange({
      ...current,
      [key]: newValue,
    });
    fireToggleEvent(key, newValue);
  };

  return (
    <Column data-testid="auto-confirm-field" data-cy="auto-confirm-field" gap={12}>
      <Row alignItems="center" gap={6} marginBottom={8}>
        <Text bold fontSize={18} color="neutral_600">
          Auto-Confirm:
        </Text>
        <SmartTooltip position="bottom" content={TOOLTIP_CONTENT}>
          <InfoIcon color={Colors.neutral_300} />
        </SmartTooltip>
      </Row>

      <Row alignItems="center" gap={12} data-testid="favorites-toggle" data-cy="favorites-toggle">
        <ReactSwitch
          checked={field.value?.favorites ?? false}
          onChange={() => handleToggle('favorites')}
          onColor={Colors.favorites_400}
          onHandleColor={Colors.white}
          offColor={Colors.neutral_100}
          handleDiameter={20}
          uncheckedIcon={false}
          checkedIcon={false}
          height={25}
          width={50}
          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
        />
        <Text fontSize={16} color="neutral_600">
          Favorites
        </Text>
      </Row>

      <Row alignItems="center" gap={12} data-testid="eligible-toggle" data-cy="eligible-toggle" marginTop={8}>
        <ReactSwitch
          checked={field.value?.eligibleProfessionals ?? false}
          onChange={() => handleToggle('eligibleProfessionals')}
          onColor={Colors.secondary_500}
          onHandleColor={Colors.white}
          offColor={Colors.neutral_100}
          handleDiameter={20}
          uncheckedIcon={false}
          checkedIcon={false}
          height={25}
          width={50}
          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
        />
        <Text fontSize={16} color="neutral_600">
          Eligible Professionals
        </Text>
      </Row>

      <Text fontSize={16} color="neutral_500" data-testid="auto-confirm-message" data-cy="auto-confirm-message" marginTop={8}>
        {autoFillInfo.message}
      </Text>
    </Column>
  );
};
